import { useSolidAuth } from "@ldo/solid-react";
import { useTranslation } from "react-i18next";
import { createContext, Dispatch, FunctionComponent, ReactNode, useContext, useReducer, useEffect } from "react";
import { discoverInbox } from "./ImportInboxContent";
import appInfo from "../app-info.json";
import sampleQueries from "../utils/example-queries";

const AppContextContext = createContext<AppContextPair | undefined>(undefined);

// A custom-element allowing its descendant to use `useAppContext`.
export const AppContext: FunctionComponent<{ children: ReactNode }> = ({ children }) => {
  const { i18n } = useTranslation();
  const { session } = useSolidAuth();

  const pair = useReducer(dispatchAppContext, initAppContext(i18n.changeLanguage));
  const [, dispatch] = pair;

  // to discover inbox 
  useEffect(() => {
    (async () => {
      try {
        if (!session.webId) return;
        console.debug("Discovering inbox for", session.webId);

        const inboxUrl = await discoverInbox(session.webId);
        if (!inboxUrl) {
          console.warn("No inbox found for user", session.webId);
          return;
        }

        dispatch({ type: "setInboxUrl", url: inboxUrl });

      } catch (error) {
        console.error("Error discovering inbox:", error);
      }
    })();
  }, [session.webId, dispatch]);

  return <AppContextContext.Provider value={pair}>
    {children}
  </AppContextContext.Provider>
};

// Get access to the application context (see `AppContextSingleton`)
// and a dispatch function to modify it (see `AppContextAction`).
export function useAppContext(): AppContextPair {
  return useContext(AppContextContext) as AppContextPair;
}

// The singleton-class of the application context.
export class AppContextSingleton {
  // Preferences
  preferences = new AppPreferencesSingleton();
  // Indicates whether the login is still in progress (in particular, if the full profile is still loading).
  //
  // Note that session.isLoggedIn will be true before this flag comes back to `false` after a call to `login`.
  loginInProgress = false;
  // A function for changing the language in the environment
  changeLanguage = (_: string) => { };
  // current user's inbox URL
  inboxUrl: string = "";
  // Sparql Engine
  sparqlEngine: SparqlEngine | null = null;
  // Useful for debugging purposes.
  debug = 0;

  // methods:
  // (NB: can not use "proper" methods, this prevents us from using the spread syntax in dispatcher)

  // Constructs a valid locale from preferences
  locale = function (this: AppContextSingleton) {
    if (this._localeCache == null) {
      this._localeCache = computeLocale(this);
      console.debug("AppContextSingleton.locale", this._localeCache)
    }
    return this._localeCache;
  }

  // private attribute for memoizing the result of locale()
  _localeCache: string | null = null;
}

// To get the browser language or English as the default language.
export const getBrowserLanguage = (): string => {
  const browserLang = navigator.language;
  const langCode = browserLang.split('-')[0];
  const supportedLanguages = Object.keys(appInfo.locales);
  return supportedLanguages.includes(langCode) ? langCode : 'en';
};

// The singleton-class of the application preferences.
export class AppPreferencesSingleton {
  // The preferred language to use
  language = getBrowserLanguage();
  // Locale suffix
  localeSuffix = "";
  // User queries for Sparnatural
  queries: Record<string, string> = sampleQueries;
}

function initAppContext(changeLanguage: (lang: string) => void): AppContextSingleton {
  console.debug("initAppContext starting");
  let ret = new AppContextSingleton();
  ret.changeLanguage = changeLanguage;

  // detecting if login is in progress
  //  NB: detecting loging paramaters in URL is not entirely relibable,
  //      because the page is sometimes re-rendered without the parameter before the profile had a change to load completely,
  //      so we "cache" this hint in module variable LOGIN_IN_PROGRESS
  if (window.location.search !== "") {
    console.debug("initAppContext: detecting login parameters in URL");
    ret.loginInProgress = true;
    LOGIN_IN_PROGRESS = true;
  } else if (LOGIN_IN_PROGRESS) {
    console.debug("initAppContext: module variable LOGIN_IN_PROGRESS was set");
    ret.loginInProgress = true;
  } else {
    LOGIN_IN_PROGRESS = false;
  }
  return ret;
}

// The actions one can dispatch to modify the application context (see `useAppContext`).
//
// They should be self-explanatory.
export type AppContextAction =
  { type: "setLanguage", value: string } |
  { type: "setLocaleSuffix", value: string } |
  { type: "setQueries", value: Record<string, string> } |
  { type: "setLoginInProgress" } |
  { type: "unsetLoginInProgress"} |
  { type: "setPreferences"; preferences: Record<string, any> } |
  { type: "setInboxUrl", url: string } |
  { type: "setSparqlEngine", sparqlEngine: SparqlEngine } |
  { type: "incDebug" };


function dispatchAppContext(old: AppContextSingleton, action: AppContextAction): AppContextSingleton {
  let preferences;
  switch (action.type) {
    case "setPreferences":
      console.debug("AppContex: setPreferences", action.preferences);
      preferences = { ...old.preferences, ...action.preferences };
      return { ...old, preferences, _localeCache: null};
    case "setLanguage":
      console.debug("AppContex: setLanguage", action.value);
      old.changeLanguage(action.value);
      preferences = { ...old.preferences, language: action.value };
      return { ...old, preferences, _localeCache: null};
    case "setLocaleSuffix":
      console.debug("AppContex: setLocaleSuffix", action.value);
      preferences = { ...old.preferences, localeSuffix: action.value };
      return { ...old, preferences, _localeCache: null};
    case "setQueries":
      console.debug("AppContex: setQueries", action.value);
      preferences = { ...old.preferences, queries: action.value };
      return { ...old, preferences, _localeCache: null};
    case "setLoginInProgress":
      console.debug("AppContex: setLoginInProgress");
      return { ...old, loginInProgress: true };
    case "unsetLoginInProgress":
      console.debug("AppContex: unsetLoginInProgress");
      return { ...old, loginInProgress: false };
    case "setInboxUrl":
      console.debug("AppContext: setInboxUrl", action.url);
      return { ...old, inboxUrl: action.url };
    case "setSparqlEngine":
      console.debug("AppContext: setSparqlEngine", action.sparqlEngine);
      return { ...old, sparqlEngine: action.sparqlEngine };
    case "incDebug":
      console.debug("AppContex: incDebug");
      return { ...old, debug: old.debug + 1 };
  }
}

type AppContextPair = [AppContextSingleton, Dispatch<AppContextAction>];

function computeLocale(appCtx: AppContextSingleton): string {
  console.debug("computeLocale");
  const language = appCtx.preferences.language;
  const suffix = appCtx.preferences.localeSuffix;
  if (suffix) {
    const locale = language + "-" + suffix;
    try {
      (42).toLocaleString(locale);
      return locale;
    }
    catch { }
  }
  try {
    (42).toLocaleString(language);
    return language;
  }
  catch { }
  return 'en';
}

let LOGIN_IN_PROGRESS = false;

export type SparqlEngine = {
    clear: (callback: () => void, errorCallback: (error: any) => void) => void;
    populate: (quads: object[],  callback: (size: number) => void, errorCallback: (error: any) => void) => void;
    executeSparql: (sparql: string, callback: (data: {}) => void, errorCallback: (error: any) => void) => void;
};
